import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Select, DatePicker, Button, Alert, Divider, Typography, Row, Col, message } from 'antd';
import { DollarOutlined, ScheduleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { paymentService } from '../../services/paymentService';

const { Text } = Typography;

interface PaymentPlanFormProps {
  totalAmount: number;
  entityType: 'estimate' | 'work_order';
  entityId: number;
  customerId: number;
  onSuccess: () => void;
}

const PaymentPlanForm: React.FC<PaymentPlanFormProps> = ({
  totalAmount,
  entityType,
  entityId,
  customerId,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downPayment, setDownPayment] = useState(0);
  const [numberOfInstallments, setNumberOfInstallments] = useState(4);
  const [minPaymentPlanAmount, setMinPaymentPlanAmount] = useState(500);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await paymentService.getSettings();
      setMinPaymentPlanAmount(settings.min_payment_plan_amount);

      if (settings.require_down_payment) {
        const minDown = (totalAmount * settings.min_down_payment_percentage) / 100;
        setDownPayment(minDown);
        form.setFieldValue('downPayment', minDown);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      setError(null);

      await paymentService.createPaymentPlan({
        entityType,
        entityId,
        customerId,
        totalAmount,
        downPayment: values.downPayment || 0,
        numberOfInstallments: values.numberOfInstallments,
        frequency: values.frequency,
        startDate: values.startDate.toISOString(),
        notes: values.notes
      });

      message.success('Payment plan created successfully');
      onSuccess();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to create payment plan';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const financedAmount = totalAmount - downPayment;
  const installmentAmount = numberOfInstallments > 0 ? financedAmount / numberOfInstallments : 0;

  const handleValuesChange = (changedValues: any) => {
    if ('downPayment' in changedValues) {
      setDownPayment(changedValues.downPayment || 0);
    }
    if ('numberOfInstallments' in changedValues) {
      setNumberOfInstallments(changedValues.numberOfInstallments);
    }
  };

  if (totalAmount < minPaymentPlanAmount) {
    return (
      <Alert
        message="Payment Plan Not Available"
        description={`Payment plans are only available for amounts of $${minPaymentPlanAmount.toFixed(2)} or more.`}
        type="warning"
        showIcon
      />
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={handleValuesChange}
      initialValues={{
        downPayment: downPayment,
        numberOfInstallments: 4,
        frequency: 'monthly',
        startDate: dayjs().add(1, 'month')
      }}
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="Payment Plan"
        description="Set up a payment plan with flexible installment options. Customers will receive payment reminders before each due date."
        type="info"
        style={{ marginBottom: 16 }}
        showIcon
      />

      <Form.Item
        label="Down Payment (Optional)"
        name="downPayment"
      >
        <InputNumber
          style={{ width: '100%' }}
          prefix={<DollarOutlined />}
          min={0}
          max={totalAmount}
          precision={2}
          step={10}
          size="large"
        />
      </Form.Item>

      <Form.Item
        label="Number of Installments"
        name="numberOfInstallments"
        rules={[
          { required: true, message: 'Please select number of installments' },
          { type: 'number', min: 2, message: 'Minimum 2 installments' },
          { type: 'number', max: 12, message: 'Maximum 12 installments' }
        ]}
      >
        <Select size="large">
          <Select.Option value={2}>2 payments</Select.Option>
          <Select.Option value={3}>3 payments</Select.Option>
          <Select.Option value={4}>4 payments</Select.Option>
          <Select.Option value={6}>6 payments</Select.Option>
          <Select.Option value={12}>12 payments</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Payment Frequency"
        name="frequency"
        rules={[{ required: true, message: 'Please select payment frequency' }]}
      >
        <Select size="large">
          <Select.Option value="weekly">Weekly</Select.Option>
          <Select.Option value="biweekly">Bi-weekly (Every 2 weeks)</Select.Option>
          <Select.Option value="monthly">Monthly</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="First Payment Date"
        name="startDate"
        rules={[{ required: true, message: 'Please select first payment date' }]}
      >
        <DatePicker
          style={{ width: '100%' }}
          size="large"
          disabledDate={(current) => current && current < dayjs().startOf('day')}
        />
      </Form.Item>

      <Divider />

      <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Total Amount</Text>
            <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
              ${totalAmount.toFixed(2)}
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary">Down Payment</Text>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1890ff' }}>
              ${downPayment.toFixed(2)}
            </div>
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Amount Financed</Text>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#52c41a' }}>
              ${financedAmount.toFixed(2)}
            </div>
          </Col>
          <Col span={12}>
            <Text type="secondary">Per Installment</Text>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fa8c16' }}>
              ${installmentAmount.toFixed(2)}
            </div>
          </Col>
        </Row>
      </div>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={loading}
          icon={<ScheduleOutlined />}
          block
        >
          Create Payment Plan
        </Button>
      </Form.Item>

      <div style={{ marginTop: 12, textAlign: 'center', color: '#8c8c8c', fontSize: '12px' }}>
        <p>Customer will receive payment reminders via email before each due date.</p>
      </div>
    </Form>
  );
};

export default PaymentPlanForm;
